import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSpinner, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { ALL_COLUMNS } from '../../utils/tableConstants';
import { formatValue } from '../../utils/excelUtils';
import { useNavigate } from "react-router-dom";
import PlacementDetailsModal from './PlacementDetailsModal';
import { useToast } from '../Admissionform/ToastManager';

const DataTable = ({
  currentItems,
  selectedColumns,
  indexOfFirstItem,
  selectedStudents = new Set(),
  onStudentSelection,
  onSelectAll,
  type = "placementEligible"
}) => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [downloadingStates, setDownloadingStates] = useState({});
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const { showWarning, showError, showSuccess } = useToast();
  const navigate = useNavigate();


  const percentFields = [
    'project1Score',
    'project2Score',
    'project3Score',
    'communicationScore',
    'technicalScore',
    'mockTest1Score',
    'mockTest2Score',
    'mockTest3Score'
  ];


  const handleDownloadClick = async (rowIndex) => {
    const student = currentItems[rowIndex];
    const studentId = student.studentId;

    if (!studentId) {
      showError('Student ID not found. Cannot download resume.');
      return;
    }

    setDownloadingStates(prev => ({
      ...prev,
      [rowIndex]: true
    }));

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/download/${studentId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`Failed to download resume: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('Content-Type');

      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.hasResume === false) {
          showWarning(data.message || 'No resume found for this student.');
        } else {
          showError(data.message || 'Unexpected response from server.');
        }
        return;
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${student.studentName || 'student'}_${student.Department}_${student.branch}_resume.pdf`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log(`Resume downloaded successfully for ${student.studentName}`);
    } catch (error) {
      console.error('Error downloading resume:', error);
      showError(`Failed to download resume: ${error.message}`);
    } finally {
      setDownloadingStates(prev => ({
        ...prev,
        [rowIndex]: false
      }));
    }
  };

  // Handle individual student selection
  const handleStudentCheck = (studentId, isChecked) => {
    if (onStudentSelection) {
      onStudentSelection(studentId, isChecked);
    }
  };

  // Handle select all checkbox
  const handleSelectAllCheck = (isChecked) => {
    if (onSelectAll) {
      onSelectAll(isChecked);
    }
  };

  // Check if all current page students are selected
  const isAllCurrentPageSelected = () => {
    return currentItems.length > 0 && currentItems.every(student =>
      selectedStudents.has(student.studentId)
    );
  };

  // Check if some current page students are selected
  const isSomeCurrentPageSelected = () => {
    return currentItems.some(student => selectedStudents.has(student.studentId));
  };

  // Handle bulk download
  const handleBulkDownload = async () => {
    if (selectedStudents.size === 0) {
      showError('Please select at least one student to download resumes.');
      return;
    }

    setIsBulkDownloading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/resumes/download-bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          studentIds: Array.from(selectedStudents)
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download resumes');
      }

      // Handle zip file download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const currentDate = new Date().toISOString().split('T')[0];
      link.download = `Selected_Resumes_${currentDate}.zip`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(`Successfully downloaded ${selectedStudents.size} resume(s)!`);

      // Optionally clear selection after download
      if (onSelectAll) {
        onSelectAll(false);
      }

    } catch (error) {
      console.error('Error downloading bulk resumes:', error);
      showError(`Failed to download resumes: ${error.message}`);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const closeModal = () => {
    setShowViewModal(false);
    setSelectedRecord(null);
  };

  return (
    <>
      {/* Bulk Actions Bar */}
      {selectedStudents.size > 0 && (
        <div className="bulk-actions-bar">
          <div className="selected-count">
            {selectedStudents.size} student(s) selected
          </div>
          <div className="bulk-actions">
            <button
              onClick={handleBulkDownload}
              className="bulk-download-btn"
              disabled={isBulkDownloading}
              title="Download selected resumes as ZIP"
            >
              {isBulkDownloading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} />
                  Download Selected ({selectedStudents.size})
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="records-table">
          <thead>
            <tr>
              <th className="select-column">
                <input
                  type="checkbox"
                  checked={isAllCurrentPageSelected()}
                  ref={input => {
                    if (input) {
                      input.indeterminate = isSomeCurrentPageSelected() && !isAllCurrentPageSelected();
                    }
                  }}
                  onChange={(e) => handleSelectAllCheck(e.target.checked)}
                  title="Select all students on this page"
                />
              </th>
              {ALL_COLUMNS
                .filter(column => selectedColumns.includes(column.key))
                .map(column => (
                  <th key={column.key}>{column.label}</th>
                ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={selectedColumns.length + 2} style={{ textAlign: 'center', padding: '20px' }}>
                  No records found
                </td>
              </tr>
            ) : (
              currentItems.map((item, rowIndex) => {
                // ✅ Safe check for resumePath
                const resumePath = item?.resumePath || item?.studentRegistration?.resumePath;
                const hasResume = !!resumePath;

                return (
                  <tr
                    key={item.id}
                    className={`
                        ${resumePath == null || resumePath === "" ? "no-resume-row" : ""}
            ${item.ProgressStatus === "In Active" ? "inactive-row" : ""}
            ${selectedStudents.has(item.studentId) ? "selected-row" : ""}
                ${item.placementInterest === "Critical"
                        ? "critical-row"
                        : item.ProgressStatus === "Course Completed"
                          ? "completed-row"
                          : ""
                      }
          `}
                  >
                    <td className="select-column">
                      <input
                        type="checkbox"
                        checked={selectedStudents.has(item.studentId)}
                        onChange={(e) => handleStudentCheck(item.studentId, e.target.checked)}
                        title="Select this student"
                      />
                    </td>

                    {ALL_COLUMNS
                      .filter(column => selectedColumns.includes(column.key))
                      .map(column => (
                        <td
                          key={column.key}
                          className="table-cell"
                          title={
                            item.placementInterest === "Critical" && item.staffRemarks
                              ? item.staffRemarks
                              : ""
                          }
                        >                          {
                            column.key === "qualification"
                              ? [item.ugDegree, item.pgDegree].filter(Boolean).join(", ")
                              : column.key === "passedYear"
                                ? [item.ugPassout, item.pgPassout].filter(Boolean).join(", ")
                                : percentFields.includes(column.key)
                                  ? item[column.key] != null
                                    ? `${item[column.key]}%`
                                    : "N/A"
                                  : formatValue(item[column.key])
                          }
                        </td>
                      ))}

                    <td className="action-buttons">
                      <button
                        onClick={() => {
                          const editPath =
                            type === "placementEligible"
                              ? `/edits-student/${item.id}`
                              : `/edit-student/${item.id}`;

                          navigate(editPath);
                        }}
                        className="edit-btn"
                        title="Edit Record"
                      >
                        <FontAwesomeIcon icon={faPenToSquare} />
                      </button>
                      {resumePath && (
                        <button
                          onClick={() => handleDownloadClick(rowIndex)}
                          className="editpp-btn"
                          title="Download Resume"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </button>
                      )}
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {/* Placement Details Modal */}
      <PlacementDetailsModal
        isOpen={showViewModal}
        onClose={closeModal}
        selectedRecord={selectedRecord}
      />
    </>
  );
};

export default React.memo(DataTable);